// §4.10 UoM Conversions (office, settings) — a READ-ONLY POINTER.
//
// Conversion factors are owned and edited by Module 01 (spec 06 §2.10: "UoM
// conversion CRUD lives in Module 01"), so this screen deliberately has no
// write path. It does two things:
//   1. deep-links into the product's own UoM & conversions tab
//      (/catalogue/products/{id}#uom — the tab the catalogue screen selects
//      from the hash), and
//   2. renders the §9.6 warning list: products with movements posted in a UoM
//      that has no conversion to the product's base UoM. Those are exactly the
//      products where the next movement in that unit will be refused
//      `UOM_NOT_CONVERTIBLE`, and where an already-posted heterogeneous history
//      makes a level read ambiguous.
//
// The warning list is computed from a BOUNDED window of the ledger (the most
// recent `SCAN_LIMIT` movements) because there is no server-side "products with
// heterogeneous history" report. The window is stated on screen — a silent
// partial scan presented as a complete audit would be the dishonest version.

import { useEffect, useMemo } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { DataTable } from "../../../../components/data-table";
import { HelpTip } from "../../../../components/help-tip";
import { EmptyState, ErrorState, TableSkeleton } from "../../../../components/states";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import {
  useMovements,
  useProductConversions,
  useProductIndex,
  useUomIndex,
} from "../hooks/inventory-queries";
import { useCataloguePermissions } from "../../catalogue/hooks/permissions";

const SCAN_LIMIT = 200;

interface Finding {
  readonly productId: string;
  readonly sku: string;
  readonly baseUomCode: string;
  readonly missingUoms: readonly string[];
}

export function UomConversionsScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // §7 maps conversion EDITING to Module 01's grant, not to any inventory
  // code: `inventory.read` is held by every internal role and says nothing
  // about whether this caller can change a factor, so a floor_worker was being
  // told they could edit conversions they cannot.
  const cataloguePerms = useCataloguePermissions();

  const movements = useMovements({ limit: SCAN_LIMIT });
  const uoms = useUomIndex();

  const rows = useMemo(() => movements.data?.items ?? [], [movements.data]);
  const productIds = useMemo(
    () =>
      Array.from(new Set(rows.flatMap((row) => (row.product_id !== null ? [row.product_id] : [])))),
    [rows],
  );
  const products = useProductIndex(productIds);
  const conversions = useProductConversions(productIds);

  useEffect(() => {
    if (movements.error instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [movements.error, navigate]);

  const uomById = useMemo(() => {
    const map = new Map<string, string>();
    for (const uom of uoms.data?.items ?? []) map.set(uom.id, uom.code);
    return map;
  }, [uoms.data]);
  const uomIdByCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const uom of uoms.data?.items ?? []) map.set(uom.code, uom.id);
    return map;
  }, [uoms.data]);

  const findings = useMemo<Finding[]>(() => {
    if (products.isPending || conversions.isPending || uoms.data === undefined) return [];
    // product → the set of movement UoM codes that are not its base.
    const seen = new Map<string, Set<string>>();
    for (const row of rows) {
      if (row.product_id === null) continue;
      const product = products.byId.get(row.product_id);
      if (product === undefined) continue;
      const baseCode = uomById.get(product.base_uom_id);
      if (baseCode === undefined || baseCode === row.uom) continue;
      const set = seen.get(row.product_id);
      if (set === undefined) seen.set(row.product_id, new Set([row.uom]));
      else set.add(row.uom);
    }

    const out: Finding[] = [];
    for (const [productId, codes] of seen) {
      const product = products.byId.get(productId);
      if (product === undefined) continue;
      const baseCode = uomById.get(product.base_uom_id) ?? "";
      const factors = conversions.byId.get(productId) ?? [];
      const missing: string[] = [];
      for (const code of codes) {
        const fromId = uomIdByCode.get(code);
        // A factor in EITHER direction resolves (§2.10 takes the exact
        // reciprocal), so only a pair with neither is genuinely missing.
        const covered =
          fromId !== undefined &&
          factors.some(
            (factor) =>
              (factor.from_uom_id === fromId && factor.to_uom_id === product.base_uom_id) ||
              (factor.to_uom_id === fromId && factor.from_uom_id === product.base_uom_id),
          );
        if (!covered) missing.push(code);
      }
      if (missing.length > 0) {
        out.push({ productId, sku: product.sku, baseUomCode: baseCode, missingUoms: missing });
      }
    }
    return out.sort((a, b) => a.sku.localeCompare(b.sku));
  }, [rows, products, conversions, uomById, uomIdByCode, uoms.data]);

  const canEdit = cataloguePerms.has("catalogue.product.write");
  const scanning = movements.isPending || products.isPending || conversions.isPending;

  return (
    <section className="uom-conversions">
      <PageHeader
        title={t("inventory.uom.title")}
        actions={
          <>
            <HelpTip
              label={t("nav.helpAbout", { topic: t("inventory.uom.title") })}
              data-testid="uom-owned-by-catalogue"
            >
              {t("inventory.uom.ownedByCatalogue")}
            </HelpTip>
          </>
        }
      />

      {movements.error && !(movements.error instanceof UnauthorizedError) ? (
        <ErrorState
          title={t("inventory.uom.errorTitle")}
          detail={movements.error.message}
          retryLabel={t("inventory.common.retry")}
          onRetry={() => void movements.refetch()}
          requestId={requestIdOf(movements.error)}
          requestIdLabel={t("inventory.common.requestId")}
        />
      ) : null}

      {scanning ? (
        <TableSkeleton rows={4} cols={3} label={t("inventory.uom.scanning")} />
      ) : (
        <>
          <p className="inv-hint">{t("inventory.uom.window", { n: SCAN_LIMIT })}</p>
          {findings.length === 0 ? (
            <EmptyState title={t("inventory.uom.cleanTitle")} body={t("inventory.uom.cleanBody")} />
          ) : (
            <>
              <p className="degraded-note is-warning" role="note">
                {t("inventory.uom.warning", { n: findings.length })}
              </p>
              <DataTable
                columns={[
                  { key: "sku", header: t("inventory.uom.colProduct"), id: true },
                  { key: "baseUomCode", header: t("inventory.uom.colBase") },
                  {
                    key: "missingUoms",
                    header: t("inventory.uom.colMissing"),
                    render: (finding) => finding.missingUoms.join(", "),
                  },
                  {
                    key: "fix",
                    header: t("inventory.uom.colFix"),
                    actions: true,
                    // The deep link the §4.10 row promises: Module 01's product
                    // detail, UoM & conversions tab.
                    render: (finding) => (
                      <Link
                        to="/catalogue/products/$id"
                        params={{ id: finding.productId }}
                        hash="uom"
                        className="ds-lnk"
                      >
                        {canEdit
                          ? t("inventory.uom.openConversions")
                          : t("inventory.uom.viewConversions")}
                      </Link>
                    ),
                  },
                ]}
                rows={findings}
                rowKey={(finding) => finding.productId}
                empty={null}
              />
            </>
          )}
        </>
      )}
    </section>
  );
}
